import React, { useEffect, useRef, useMemo } from 'react';
import * as echarts from 'echarts';
import useIntlExtend from '@/pages/intelligence/hooks/useIntlExtend';
import moment from 'moment';
import { isNumber, upperFirst, isFunction } from 'lodash-es';
import { ratioValueFormat, financeFormat } from '@/pages/home/utils/common';
// import { mathFormat } from '@/pages/intelligence/utils/commonUtils';
import { numStr } from '@/pages/intelligence/pages/_config/topchartsInterface';
import ChartBubble from '@/pages/intelligence/components/ChartBubble';
import { feachTimeTypeToFilterTimeType } from '@/pages/intelligence/utils/common';
import {
  PLATFORM,
} from '@/pages/intelligence/pages/TopCharts/MobileGames/components/GamesDetails/Overview/PerformanceSummary/cardUtils';

import './index.less';

interface ILineData {
  xAxisData?: string[];
  seriesData?: (string | number)[];
  weekDate?: string[];
  predictData?: string[];
  lineDom?: string;
  dataType?: 'month' | 'week' | 'day';
  xAxisLabelInterval?: number;
}

interface ICardLineChartProps {
  // y轴是否反转，默认不反转
  yAxisInverse?: boolean;
  // y轴最小值，默认undefined
  yAxisMin?: number;
  // y轴刻度文案formatter
  yAxisFormatter?: (payload?: number) => any;
  // 是否开启折线图阴影堆叠，默认开启
  enableSeriesAreaStyle?: boolean;
  // 通用气泡显示指标名
  titleContent?: string;
  // companies tooltip 气泡中显示指标名
  metricTitleLocaleKey?: string;
  // 折线图数据
  lineData: ILineData;
  // PHeat 使用属性
  rankData?: { [key: string]: any };
  baseIndicators?: { indicatorsVal: string };
  // 根据不同的入口 moduleName 特殊处理，使用不同的 tooltip formatter 方法
  moduleName?: 'mobileGames' | 'pcGames' | 'pcConsoleGames' | 'companies' | 'marketProfile';
  // 是否展示 marker 圆点
  isPolishing?: boolean;
  // 是否需要调整 tooltip 浮窗的位置，默认 true
  isAdustTooltipPosition?: boolean;
  // 格式化方式
  isRatioValueFormat?: boolean;
  // 根据 icon 名称获取对应数据源平台名称
  icon?: string;
  // 是否不展示 icon 数据源平台名
  noSourcePlatform?: boolean;

  isRatioValueFormatNoPrecent?: boolean;
  /** 全部展示 x 轴标签的断点宽度（默认 1440） */
  fullLabelBreakpoint?: number;
}

const SERIES_SHADOW_AREA_STYLE = {
  // 折线区域颜色
  color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
    {
      offset: 0,
      color: 'rgba(0,118,255,0.30)',
    },
    {
      offset: 1,
      color: 'rgba(209,230,250,0.00)',
    },
  ]),
};

export default function CardLineChart(props: ICardLineChartProps) {
  const {
    yAxisInverse = false,
    yAxisMin,
    yAxisFormatter,
    enableSeriesAreaStyle = true,
    titleContent = '',
    metricTitleLocaleKey = '',
    lineData = {},
    rankData = {},
    baseIndicators = {},
    icon,
    moduleName,
    isPolishing,
    // 是否需要调整 tooltip 浮窗的位置，默认 true
    isAdustTooltipPosition = true,
    isRatioValueFormat = false,
    isRatioValueFormatNoPrecent = false,
    noSourcePlatform = false,
    fullLabelBreakpoint = 1440,
  } = props;

  // console.log('CardLineChart', props);
  const { locale } = useIntlExtend();
  const {
    xAxisData = [],
    weekDate = [],
    seriesData = [],
    predictData = [],
    dataType: dataTypeLy = 'month',
    lineDom = '',
    xAxisLabelInterval,
  } = lineData;

  const dataType = useMemo(() => feachTimeTypeToFilterTimeType(dataTypeLy), [dataTypeLy]);

  // 判断是否为 Mac 设备，字体调小一号，避免文案底部溢出遮挡
  const isMac = navigator.userAgent.indexOf('Mac') !== -1;

  const getTypeDate = (date: string, dataType: string) => {
    if (dataType === 'Month') {
      return moment(date).format('YYYY-MM');
    }
    return moment(date).format('YYYY-MM-DD');
  };

  const singleLineTooltip = (params: any) => {
    const { xLabel, value, dataType, isPredict, weekDate } = params?.[0]?.data;
    let showXLabel = ''; // 显示 x 轴 label
    let showValue = ''; // 显示具体数值
    showXLabel = dataType === 'week' ? weekDate : getTypeDate(xLabel, upperFirst(dataType));
    // showValue = mathFormat(value, true) || '';
    // showValue = String(mathFormat(value, true));
    showValue = String(
      isRatioValueFormat
        ? ratioValueFormat(value, 2, false, 0, isRatioValueFormatNoPrecent, 3)
        : financeFormat(value, 2, false, 0),
    );

    return ChartBubble({
      topChartsType: 'overview-card',
      chartType: 'line',
      showXLabel: '', // 清空 showXLabel，避免被 getTitleValue 使用
      secondTitleLeft: showXLabel, // 第一行显示时间
      otherTitleLeft: titleContent, // 第二行左侧显示指标标题
      otherTitleRight: showValue, // 第二行右侧显示数值
      leftContent: isPredict ? 'by DataBrain' : '',
      rightContent: '',
    });
  };

  // 含补齐数据 - 折线图 tooltip 自定义函数
  const lineTooltip = (params: any) => {
    // tooltip trigger 为 axis 时，formatter 回调函数中的参数为 series 数组
    const currentItem = params?.[0];
    const { xLabel, value, dataType, isPredict, weekDate } = currentItem?.data || {};
    let showXLabel = ''; // 显示 x 轴 label
    let showValue = ''; // 显示具体数值
    showXLabel = dataType === 'week' ? weekDate : getTypeDate(xLabel, upperFirst(dataType));
    // showValue = mathFormat(value, true) || '';
    showValue =
      (isRatioValueFormat
        ? ratioValueFormat(value, 2, false, 0, isRatioValueFormatNoPrecent, 3)
        : financeFormat(value, 2, false, 0)) || '';
    // showValue = mathFormat(value, true) || '';
    const showMarket = isPolishing ? `<span>${currentItem?.marker}</span>` : '';
    // eslint-disable-next-line no-nested-ternary
    const detailContent = isPredict
      ? `Predicted ${titleContent}`
      : noSourcePlatform
      ? titleContent
      : `${icon ? PLATFORM[icon] : ''} ${titleContent}`;

    return ChartBubble({
      topChartsType: 'overview-card',
      chartType: 'line',
      showXLabel: '', // 清空 showXLabel，避免被 getTitleValue 使用
      secondTitleLeft: showXLabel, // 第一行显示时间
      otherTitleLeft: detailContent, // 第二行左侧显示指标标题
      otherTitleRight: showValue + (titleContent === 'Retention D2' ? '%' : ''), // 第二行右侧显示数值
      leftContent: '',
      rightContent: '',
    });
  };

  // 公司详情页 Summary 页签 Game Performance Summary 模块的 tooltip
  const lineTooltipFormatterOfCompanies = (params: any) => {
    const currentItem = params?.[0];
    const { xLabel, value, dataType, isPredict, weekDate } = currentItem?.data || {};
    let showXLabel = ''; // 显示 x 轴 label
    let showValue = ''; // 显示具体数值
    showXLabel = dataType === 'week' ? weekDate : getTypeDate(xLabel, upperFirst(dataType));
    // showValue = mathFormat(value, true) || '';
    showValue =
      (isRatioValueFormat
        ? ratioValueFormat(value, 2, false, 0, isRatioValueFormatNoPrecent, 3)
        : financeFormat(value, 2, false, 0)) || '';
    // showValue = mathFormat(value, true) || '';
    const showMarket = isPolishing ? `<span>${currentItem?.marker}</span>` : '';
    const detailContent = metricTitleLocaleKey;

    return ChartBubble({
      topChartsType: 'overview-card',
      chartType: 'line',
      showXLabel: '', // 清空 showXLabel，避免被 getTitleValue 使用
      secondTitleLeft: showXLabel, // 第一行显示时间
      otherTitleLeft: detailContent, // 第二行左侧显示指标标题
      otherTitleRight: showValue + (titleContent === 'Retention D2' ? '%' : ''), // 第二行右侧显示数值
      leftContent: '',
      rightContent: '',
    });
  };

  // MarketProfile Tooltip 气泡展示
  const marketProfileTooltipFormatter = (params: any) => {
    const { xLabel } = params?.[0]?.data;
    const showXLabel = getTypeDate(xLabel, upperFirst(dataType));
    return ChartBubble({
      data: params,
      topChartsType: 'marketProfile-overview-sum',
      showXLabel,
      headerRightTitle: titleContent,
      imageType: 'mobile',
      chartType: 'line',
      dateType: 'month',
      ifNoBarImg: true,
      lineSourceName: ' ',
    });
  };

  const tooltipFormatters: any = {
    mobileGames: lineTooltip,
    pcConsoleGames: lineTooltip,
    companies: lineTooltipFormatterOfCompanies,
    marketProfile: marketProfileTooltipFormatter,
  };

  const optionLine: any = {
    tooltip: {
      // position: moduleName !== 'mobileGames' ? undefined : function (point: any) {
      //   return [point[0] - 280, point[1] - 100];
      // },
      // 默认保持原有逻辑
      // eslint-disable-next-line no-nested-ternary
      position: isAdustTooltipPosition
        ? moduleName !== 'mobileGames'
          ? undefined
          : function (point: any) {
              return [point[0] - 280, point[1] - 100];
            }
        : undefined,
      show: titleContent !== 'PHeat', // 显示悬浮窗 // PHeat 卡片不显示悬浮窗
      // show: true, // 显示悬浮窗
      // confine: true,
      trigger: 'axis',
      axisPointer: {
        type: 'line',
        z: 0, // axisPointer 线展示在圆点下方
        lineStyle: {
          type: 'solid',
          color: '#BBBDCB',
        },
        // 展示 axisPointer label 覆盖 xAxis label 样式
        label: {
          show: true,
          color: '#3B3D4B',
          fontWeight: 600,
          backgroundColor: '#fff',
          padding: 0,
          margin: 20,
        },
      },
      borderColor: '#ffffff',
      extraCssText: 'z-index: 9999999;',
      padding: 0,
      // formatter: moduleName == 'mobileGames' ? lineTooltip : singleLineTooltip,
      formatter: moduleName
        ? tooltipFormatters[moduleName] || singleLineTooltip
        : singleLineTooltip,
      backgroundColor: 'rgba(0,0,0,0)'
    },
    xAxis: {
      show: titleContent !== 'PHeat', // 是否显示 X 轴坐标 // PHeat 卡片不显示
      // show: true, //是否显示 X 轴坐标
      type: 'category',
      data: [],
      axisTick: {
        show: false, // 去掉 x 轴刻度线
      },
      axisLine: {
        show: false, // 是否显示 x 轴线
      },
      axisLabel: {
        interval: 0,
        // Mac 字体调小一号，避免文案底部溢出遮挡
        fontSize: isMac ? 11 : 12,
        align: 'center',
        color: '#BBB',
        // fontSize: 6,
      },
    },
    yAxis: [
      {
        show: titleContent !== 'PHeat', // 是否显示 y 轴坐标 // PHeat 卡片不显示
        position: 'right', // y 轴在右侧
        type: 'value',
        axisTick: {
          show: false, // 去掉 x 轴刻度线
        },

        splitLine: {
          // 网格线颜色
          show: false, // 是否显示 y 轴网格线
        },
        axisLabel: {
          fontSize: 12,
          color: '#BBB',
          // fontSize: 8,
          formatter(value: number) {
            if (isFunction(yAxisFormatter)) return yAxisFormatter(value);
            // 处理 y 轴 label 特殊显示
            if (Math.abs(value) >= 1000000000)
              return `${Math.ceil(Math.round(value / 100000) / 10000)}B`;
            if (Math.abs(value) >= 1000000) return `${Math.ceil(Math.round(value / 10000) / 100)}M`;
            if (Math.abs(value) >= 1000) return `${Math.ceil(Math.round(value / 1000))}K`;
            if (Math.abs(value) < 1000) return Math.round(value);
          },
        },
        min: yAxisMin,
        max: yAxisInverse
          ? ({ max, min }: { max: number; min: number }) => {
              const safeMax = Number.isFinite(max) ? max : 0;
              if (safeMax === 0) return 1;
              // 排名长期稳定（max === min）时，扩展 Y 轴让趋势线不沉底
              const safeMin = Number.isFinite(min) ? min : safeMax;
              if (safeMax === safeMin) {
                const gap = safeMax - (yAxisMin ?? 1);
                // gap=0 即 Top1：+2 → 刻度 1,2,3（顶部）; gap>0 即非 Top1：+gap → 居中
                return safeMax + (gap > 0 ? gap : 2);
              }
              // 数据聚集在远离 yAxisMin 的区域时，以数据中点为基准扩展 max 使趋势线居中
              const yMin = yAxisMin ?? 1;
              const dataRange = safeMax - safeMin;
              const gapFromAxisMin = safeMin - yMin;
              if (gapFromAxisMin > 0 && gapFromAxisMin > dataRange) {
                const midpoint = (safeMax + safeMin) / 2;
                return yMin + 2 * (midpoint - yMin);
              }
              return safeMax;
            }
          : 'dataMax',
        splitNumber: 3,
        minInterval: yAxisInverse ? 1 : undefined,
        inverse: yAxisInverse,
      },
    ],
    grid: {
      right: '40px',
      left: '18px',
    },
    series: [],
  };
  const normalSeriesItem: any = {
    type: 'line',
    data: [],
    itemStyle: {
      color: '#376AE7', // 折线颜色
    },
    smooth: false,
    areaStyle:
      enableSeriesAreaStyle === false
        ? undefined
        : yAxisInverse
        ? {
            ...SERIES_SHADOW_AREA_STYLE,
            origin: 'end',
          }
        : SERIES_SHADOW_AREA_STYLE,
  };
  const pheatSeriesItem: any = {
    type: 'line',
    data: [],
    itemStyle: {
      color: '#376AE7', // 折线颜色
    },
    smooth: false,
    markLine: {
      itemStyle: {
        color: '#376AE7',
      },
      silent: true,
      lineStyle: {
        type: 'solid',
        color: '#376AE7',
        width: 2,
      },
      symbol: ['none', 'none'],
      label: {
        formatter: '{b}',
        color: '#BBB',
      },
      data: [
        {
          name: '',
          xAxis: '',
        },
      ],
    },
  };
  const predictSeriesItem: any = {
    type: 'line',
    data: [],
    itemStyle: {
      color: '#FBB545', // 折线颜色
    },
    smooth: false,
    areaStyle: {
      // 折线区域颜色
      color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
        {
          offset: 0,
          color: 'rgba(251, 181, 69, 0.3)',
        },
        {
          offset: 1,
          color: 'rgba(251, 181, 69, 0)',
        },
      ]),
    },
  };
  const ref = useRef(null);
  let echart: any = null;

  // 处理option格式
  function format() {
    const xLabel: numStr[] = [];
    const dataSource: any = [];
    const predictSource: any = [];
    // 是否有补齐数据
    const predict: boolean = predictData.some((item: boolean) => item);
    // let dataLength = seriesData?.length;
    const xLabelLength = xAxisData?.length;
    const maxSeriesCopy = seriesData.slice(0)?.filter(isNumber);
    let maxSeriesSingle = 0;
    maxSeriesCopy?.sort((a: any, b: any) => b - a);
    // eslint-disable-next-line prefer-destructuring
    maxSeriesSingle = maxSeriesCopy[0];

    // 有负值需要计算最大值与最小值的差值
    const hasNegativeValue = maxSeriesCopy.some((item) => item < 0);

    let differenceValue;
    if (hasNegativeValue) {
      const maxValue = Math.max(...maxSeriesCopy);
      const minValue = Math.min(...maxSeriesCopy);
      differenceValue = maxValue - minValue;

      // 指定 Y 轴最小值，避免默认给定的默认最小值和数据不同，导致轴标签 Y 轴最大值和数据不同
      optionLine.yAxis[0].min = minValue;
    }

    // let yLabel = optionLine.yAxis[0];
    // 处理 y 轴 label 显示个数
    // let _interval = Math.round(maxSericeSingle / 3);
    // 需求改为固定显示三个
    const intervalTmp = Number(hasNegativeValue ? differenceValue : maxSeriesSingle) / 2;
    // 排名长期稳定：所有数据值相同
    const minSeriesSingle = maxSeriesCopy[maxSeriesCopy.length - 1] ?? maxSeriesSingle;
    const isRankStableScene =
      yAxisInverse && isNumber(yAxisMin) && isNumber(maxSeriesSingle) && maxSeriesSingle === minSeriesSingle;
    // 稳定时 interval = gap 或 1（Top1 时 gap=0 用 1）→ 固定 3 个刻度且趋势线位置正确
    const rankStableGap = maxSeriesSingle - (yAxisMin ?? 1);
    // 数据聚集在远离 yAxisMin 的区域：空白区域 > 数据跨度
    const isRankFarFromMinScene =
      yAxisInverse && isNumber(yAxisMin) && isNumber(maxSeriesSingle) &&
      !isRankStableScene &&
      (minSeriesSingle - (yAxisMin ?? 1)) > (maxSeriesSingle - minSeriesSingle);
    let interval;
    if (isRankStableScene) {
      interval = rankStableGap > 0 ? rankStableGap : 1;
    } else if (isRankFarFromMinScene) {
      const yMin = yAxisMin ?? 1;
      const midpoint = (maxSeriesSingle + minSeriesSingle) / 2;
      const extendedMax = yMin + 2 * (midpoint - yMin);
      const halfRange = (extendedMax - yMin) / 2;
      interval = halfRange < 0.5 ? halfRange : Math.round(halfRange);
    } else {
      interval = intervalTmp < 0.5 ? intervalTmp : Math.round(intervalTmp);
    }

    // 将 Y 轴 interval，标签间隔尺度数据指定为计算好可以显示三个轴标签的数据
    optionLine.yAxis[0].interval = interval;

    if (titleContent !== 'PHeat') {
      // 处理 x 轴 label - 统一使用阿拉伯数字格式
      if (upperFirst(dataType) === 'Month') {
        xAxisData.forEach((item: string) => {
          xLabel.push(moment(item).format('MM'));
        });
      } else {
        // 对于所有非 Month 类型（Day, Week 等），统一显示为 DD 格式
        xAxisData.forEach((item: string) => {
          xLabel.push(moment(item).format('DD'));
        });
      }
      optionLine.series.push(normalSeriesItem);
      if (predict) {
        optionLine.series.push(predictSeriesItem);
      }
    } else {
      // PHeat 卡片的 x 轴数据都是 PHeat 值区间
      xAxisData.forEach((item: string) => {
        xLabel.push(item);
      });
      optionLine.grid.right = '0px';
      let pheatMarkValue = '';
      if (baseIndicators.indicatorsVal === '-') {
        pheatMarkValue = '0';
      } else {
        pheatMarkValue = baseIndicators.indicatorsVal;
      }
      const pheatMarkValueNum = Number(pheatMarkValue);
      if (pheatMarkValueNum <= 10) {
        optionLine.grid.left = '15px';
        pheatSeriesItem.markLine.data[0].xAxis = '0-10';
      } else if (pheatMarkValueNum > 10 && pheatMarkValueNum <= 20) {
        pheatSeriesItem.markLine.data[0].xAxis = '10-20';
      } else if (pheatMarkValueNum > 20 && pheatMarkValueNum <= 30) {
        pheatSeriesItem.markLine.data[0].xAxis = '20-30';
      } else if (pheatMarkValueNum > 30 && pheatMarkValueNum <= 40) {
        pheatSeriesItem.markLine.data[0].xAxis = '30-40';
      } else if (pheatMarkValueNum > 40 && pheatMarkValueNum <= 50) {
        pheatSeriesItem.markLine.data[0].xAxis = '40-50';
      } else if (pheatMarkValueNum > 50 && pheatMarkValueNum <= 60) {
        pheatSeriesItem.markLine.data[0].xAxis = '50-60';
      } else if (pheatMarkValueNum > 60 && pheatMarkValueNum <= 70) {
        pheatSeriesItem.markLine.data[0].xAxis = '60-70';
      } else if (pheatMarkValueNum > 70 && pheatMarkValueNum <= 80) {
        pheatSeriesItem.markLine.data[0].xAxis = '70-80';
      } else if (pheatMarkValueNum > 80 && pheatMarkValueNum <= 90) {
        pheatSeriesItem.markLine.data[0].xAxis = '80-90';
      } else if (pheatMarkValueNum > 90 && pheatMarkValueNum <= 100) {
        optionLine.grid.right = '15px';
        pheatSeriesItem.markLine.data[0].xAxis = '90-100';
      }
      pheatSeriesItem.markLine.data[0].name = rankData.numContent;
      optionLine.series.push(pheatSeriesItem);
      // optionLine.grid.left = '15px'
      // optionLine.grid.right = '15px'
    }
    // 配置信息合并x轴label
    optionLine.xAxis.data = xLabel;
    // // 屏幕小于等于 1024 像素且 x 轴数据大于两个
    // // 只显示第一个和最后一个
    // if (window.innerWidth <= 1024 && xLabel?.length > 2) {
    //   optionLine.xAxis.axisLabel.interval = xLabel?.length - 2
    // }
    // 按照三个优先级控制月份标签显示
    // 优先级1: 全部展示 (大屏幕 > 1440px)
    // 优先级2: 只展示1，4，8，12月 (中屏幕 1024px - 1440px)
    // 优先级3: 只展示第一个和最后一个 (小屏幕 <= 1024px)
    if (xAxisLabelInterval) {
      optionLine.xAxis.axisLabel.interval = (index: number) => {
        const dataLength = xAxisData?.length || 0;
        return index === 0 || index === dataLength - 1 || index % xAxisLabelInterval === 0;
      };
    } else if (window.innerWidth <= 1024) {
      // 优先级3：只显示第一个和最后一个
      optionLine.xAxis.axisLabel.interval = (index: number) => {
        return index === 0 || index === xAxisData?.length - 1;
      };
    } else if (window.innerWidth <= fullLabelBreakpoint) {
      // 优先级2：使用算法显示第1、4、8和最后一个位置的月份
      optionLine.xAxis.axisLabel.interval = (index: number) => {
        const dataLength = xAxisData?.length || 0;
        // 总是显示第一个和最后一个
        if (index === 0 || index === dataLength - 1) {
          return true;
        }
        // 使用算法：显示索引能被3整除且大于0的位置（3, 6, 9...）
        // 这样可以均匀分布显示标签
        return index > 0 && index % 4 === 0;
      };
    } else {
      // 优先级1：全部展示
      optionLine.xAxis.axisLabel.interval = 0;
    }
    // 处理 series 数据的样式，series 的 data 最后一个样式修改，宽高为 8px,8px
    let countA: number | string = 0;
    let countB: number | string = 0;
    seriesData.forEach((item: number, index: number) => {
      if (!predictData[index]) {
        // 当天没有补齐数据
        countA = item;
        countB = '-';
      } else {
        // 当天有补齐数据
        // 前一天或者后一天都是补齐数据，countA 才填 '-'
        // 两条线才能连上
        if (index !== 0 && index !== seriesData?.length) {
          // 当前不是第一天或者最后一天
          if (predictData[index - 1] && predictData[index + 1]) {
            countA = '-';
          } else {
            countA = item;
          }
        } else if (index === 0) {
          // 当前是第一天
          if (predictData[index + 1]) {
            countA = '-';
          } else {
            countA = item;
          }
        } else if (index === seriesData?.length) {
          // 当前是最后一天
          if (predictData[index - 1]) {
            countA = '-';
          } else {
            countA = item;
          }
        }
        countB = item;
      }
      dataSource.push({
        value: countA,
        // 不是 PHeat 卡片用圆
        // PHeat 卡片不用。如果是和 markLine 交汇的点，用圆。
        symbol:
          // eslint-disable-next-line no-nested-ternary
          titleContent !== 'PHeat'
            ? 'circle'
            : pheatSeriesItem.markLine.data[0].xAxis === xAxisData[index]
            ? 'circle'
            : 'none',
        symbolSize: 6,
        itemStyle: {
          // color: '#376AE7',
          // borderColor: '66ADFF',
          // borderWidth: 2,
          borderWidth: 1,
          borderColor: '#fff',
        },
        xLabel: xAxisData[index],
        weekDate: weekDate[index],
        dataType,
        isPredict: false,
      });
      // 有补齐数据
      if (predict) {
        predictSource.push({
          value: countB,
          symbol: 'circle',
          symbolSize: 6,
          itemStyle: {
            // color: '#376AE7',
            // borderColor: '66ADFF',
            borderWidth: 2,
          },
          xLabel: xAxisData[index],
          dataType,
          isPredict: true,
        });
      }
    });
    // 配置信息合并 series 的数据
    optionLine.series[0].data = dataSource;
    // 补齐数据
    if (optionLine.series[1] && predictSource?.length > 0) {
      optionLine.series[1].data = predictSource;
    }
    // echart 实例
    echart = echarts.init(ref!.current!);
    // echart = echarts.init(document.getElementById(lineDom) as HTMLElement);
    // window.addEventListener('resize', () => echart.resize());
    window.addEventListener('resize', handleResize);

    // 渲染
    optionLine && echart.setOption(optionLine);
    echart.resize();
  }

  function handleResize(e: any) {
    try {
      // 按照三个优先级控制月份标签显示
      // 优先级1: 全部展示 (大屏幕)
      // 优先级2: 只展示1，4，8，12月 (中屏幕)
      // 优先级3: 只展示第一个和最后一个 (小屏幕)
      if (e.target.innerWidth <= 1024) {
        // 优先级3：只显示第一个和最后一个
        echart.setOption({
          xAxis: {
            axisLabel: {
              interval: (index: number) => {
                if (index === 0 || index === xAxisData?.length - 1) {
                  return true;
                }
                return false;
              },
            },
          },
        });
      } else if (e.target.innerWidth <= fullLabelBreakpoint) {
        // 优先级2：使用算法显示第1、4、8和最后一个位置的月份
        echart.setOption({
          xAxis: {
            axisLabel: {
              interval: (index: number) => {
                const dataLength = xAxisData?.length || 0;
                // 总是显示第一个和最后一个
                if (index === 0 || index === dataLength - 1) {
                  return true;
                }
                // 使用算法：显示索引能被3整除且大于0的位置（3, 6, 9...）
                return index > 0 && index % 4 === 0;
              },
            },
          },
        });
      } else {
        // 优先级1：全部展示
        echart.setOption({
          xAxis: {
            axisLabel: {
              interval: 0,
            },
          },
        });
      }
      echart.resize();
    } catch (err) {}
  }

  useEffect(() => {
    format();
    return () => {
      echart.setOption({
        xAxis: {
          axisLabel: {
            interval: 0,
          },
        },
      });
      window.removeEventListener('resize', handleResize);
    };
  }, [dataType, xAxisData, locale]);

  return (
    <>
      <div ref={ref} className={`line-container`} id={lineDom}></div>
    </>
  );
}
